import ExcelJS from "exceljs";
import { saveAs } from 'file-saver';
import moment from "moment";

export async function exportSelectedStudentsExcel({
  invoices = [],
  schoolInfo = [],
  feeColumns = [],
  title = "DANH SÁCH PHIẾU THU HỌC SINH"
}) {
  const workbook = new ExcelJS.Workbook();
  const companyName = schoolInfo.length > 1 ? schoolInfo[1].replace("Tên Trường: ", "") : "OneKids";
  workbook.creator = companyName;
  workbook.created = new Date();
  workbook.modified = new Date();

  const worksheet = workbook.addWorksheet("DanhSachPhieuThu", {
    pageSetup: { paperSize: 9, orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
  });

  // Extract logo URL and remove from schoolInfo
    const filteredSchoolInfo = schoolInfo.filter(info => {
      if (typeof info === 'string' && info.startsWith("Url: ")) {
          // logoUrl removed
          return false;
      }
      return true;
  });

  // Create empty rows for header space
  for (let i = 0; i < 4; i++) {
    worksheet.addRow([]);
  }

  // --- Header ---
  filteredSchoolInfo.forEach((infoLine, index) => {
    const row = worksheet.getRow(index + 1);
    row.getCell(1).value = infoLine;
    row.getCell(1).font = { name: 'Times New Roman', bold: index === 1, size: 12 };
    worksheet.mergeCells(`A${index + 1}:F${index + 1}`);
  });

  const rowTitle = worksheet.addRow([title]);
  rowTitle.height = 30;
  rowTitle.getCell(1).font = { name: 'Times New Roman', bold: true, size: 16 };
  rowTitle.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
  
  worksheet.addRow([]); // empty row

  // --- Table Headers ---
  const standardHeaders = [
    "STT", 
    "Họ tên", 
    "Biệt danh", 
    "Mã HS", 
    "Mã phiếu thu", 
    "Đợt",
    "Tháng",
  ];

  const attendanceHeaders = [
    "Học T2-T6",
    "Học T7",
    "Học CN",
    "Nghỉ có phép (T2-T6)",
    "Nghỉ không phép (T2-T6)",
    "Nghỉ có phép (T7)",
    "Nghỉ không phép (T7)",
    "Đón muộn",
    "Số ngày theo lịch hàng tháng"
  ];

  const financeHeaders = [
    "Phải thu",
    "Thiếu/Thừa phiếu thu trước",
    "Đã thu",
    "Tiền mặt",
    "Chuyển khoản",
    "Còn lại",
    "Trạng thái",
    "Ghi chú phiếu thu"
  ];
  
  const feeHeaders = [];
  feeColumns.forEach(() => {
      feeHeaders.push("Đơn giá", "Số lượng", "Đơn vị", "Giảm giá", "Thành tiền", "Ghi chú");
  });
  
  const allHeaders = [...standardHeaders, ...attendanceHeaders, ...financeHeaders, ...feeHeaders];

  // Merge title dynamically based on columns
  worksheet.mergeCells(rowTitle.number, 1, rowTitle.number, allHeaders.length);

  // --- Super Headers ---
  const headerRow1 = worksheet.addRow([]);
  const headerRow2 = worksheet.addRow([]);
  
  let currentStartCol = 1;
  const standardFinanceHeaders = [...standardHeaders, ...attendanceHeaders, ...financeHeaders];
  standardFinanceHeaders.forEach(header => {
      worksheet.mergeCells(headerRow1.number, currentStartCol, headerRow2.number, currentStartCol);
      const cell = headerRow1.getCell(currentStartCol);
      cell.value = header;
      currentStartCol++;
  });

  feeColumns.forEach(fee => {
      worksheet.mergeCells(headerRow1.number, currentStartCol, headerRow1.number, currentStartCol + 5);
      const cell = headerRow1.getCell(currentStartCol);
      cell.value = fee.label;
      
      const subHeaders = ["Đơn giá", "Số lượng", "Đơn vị", "Giảm giá", "Thành tiền", "Ghi chú"];
      subHeaders.forEach((sub, i) => {
          headerRow2.getCell(currentStartCol + i).value = sub;
      });
      
      currentStartCol += 6;
  });

  [headerRow1, headerRow2].forEach(row => {
    row.height = 25;
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      // Ensure all merged cells get borders by iterating up to max col
      if (colNumber > allHeaders.length) return;

      cell.font = { name: 'Times New Roman', bold: true, size: 11 };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'thin' }, left: { style: 'thin' },
        bottom: { style: 'thin' }, right: { style: 'thin' }
      };
      
      const colIdx = colNumber - 1;
      if (colIdx < standardHeaders.length) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };
      } else if (colIdx < standardHeaders.length + attendanceHeaders.length) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEBE4F2' } };
      } else if (colIdx < standardHeaders.length + attendanceHeaders.length + financeHeaders.length) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2EFDA' } };
      } else {
        const feeIndex = Math.floor((colIdx - (standardHeaders.length + attendanceHeaders.length + financeHeaders.length)) / 6);
        if (feeIndex % 2 === 0) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
        } else {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCE4D6' } };
        }
      }
    });
  });

  // Cấu hình độ rộng cột
  allHeaders.forEach((headerName, index) => {
    const col = worksheet.getColumn(index + 1);
    let width = 12;
    if (index === 1) width = 25; // Họ tên
    else if (index === 2) width = 15; // Biệt danh
    else if (index === 3 || index === 4) width = 18; // Mã HS, Mã phiếu thu
    else if (index === 5 || index === 6) width = 10; // Đợt, Tháng
    else if (index >= standardHeaders.length && index < standardHeaders.length + attendanceHeaders.length) {
        width = 13; // Cột điểm danh
    }
    else if (index >= standardHeaders.length + attendanceHeaders.length && index < standardHeaders.length + attendanceHeaders.length + financeHeaders.length) {
        if (headerName === "Thiếu/Thừa phiếu thu trước") width = 26;
        else if (headerName === "Trạng thái") width = 18;
        else if (headerName === "Ghi chú phiếu thu") width = 30;
        else width = 15; // Cột tiền
    }
    else {
        // Cột khoản thu
        if (headerName === "Ghi chú") width = 30;
        else if (headerName === "Đơn vị" || headerName === "Số lượng") width = 10;
        else width = 15; 
    }
    col.width = width;
  });

  const getStatusName = (status) => {
      const map = {
        UNBILLED: "Chưa có phiếu thu",
        UNPAID: "Chưa đóng 100%",
        PARTIALLY_PAID: "Chưa đóng đủ",
        FULLY_PAID: "Đóng đủ 100%",
        OVERPAID: "Đóng thừa",
        FULLY_PAID_WITH_SURPLUS: "Đóng đủ thừa",
        PAID_WITH_DEFICIT: "Đóng đủ thiếu",
        HIDE: "Ẩn phiếu thu",
      };
      return map[status] || status || "-";
  };

  const totals = new Array(allHeaders.length).fill("");
  totals[1] = "Tổng cộng";

  const addToTotal = (index, value) => {
      if (value !== "" && value !== null && value !== undefined) {
          const num = Number(value);
          if (!isNaN(num)) {
              if (totals[index] === "") totals[index] = 0;
              totals[index] += num;
          }
      }
  };

  // --- Table Data ---
  invoices.forEach((item, index) => {
    const standardData = [
      index + 1,
      item.fullName || "",
      item.nickName || "",
      item.studentCode || "",
      item.invoiceCode || "",
      item.turn || "",
      `${item.month}/${item.year}`,
    ];

    const attendanceData = [
      item.arriveT2t6 ?? "",
      item.arriveT7 ?? "",
      item.arriveCn ?? "",
      item.absentCpT2t6 ?? "",
      item.absentKpT2t6 ?? "",
      item.absentCpT7 ?? "",
      item.absentKpT7 ?? "",
      item.leaveLater ?? "",
      item.quantityDate ?? "",
    ];

    const financeData = [
      item.sumMoney || 0,
      item.beforeMoney || 0,
      item.collectedMoney || 0,
      item.cashMoney || 0,
      item.transferMoney || 0,
      item.afterMoney || 0,
      getStatusName(item.status),
      item.note || ""
    ];

    attendanceData.forEach((val, i) => addToTotal(standardHeaders.length + i, val));
    financeData.forEach((val, i) => {
        if (i < 6) addToTotal(standardHeaders.length + attendanceHeaders.length + i, val);
    });

    const feeData = [];
    feeColumns.forEach((col, f) => {
      const detail = (item.invoiceDetails || []).find(d => d.revenueCode === col.key);
      const feeStart = standardHeaders.length + attendanceHeaders.length + financeHeaders.length + f * 6;
      
      if (detail) {
        feeData.push(detail.price || 0);
        feeData.push(detail.quantity || 0);
        feeData.push(detail.unit || "");
        
        let discountStr = "";
        if (detail.discount) {
            discountStr = detail.discountType === "PERCENT" ? `${detail.discountNumber}%` : (detail.discountNumber || 0);
            if (detail.discountType === "MONEY") addToTotal(feeStart + 3, detail.discountNumber);
        } else {
            discountStr = 0;
        }
        feeData.push(discountStr);
        feeData.push(detail.amount || 0);
        feeData.push(detail.description || "");
        
        addToTotal(feeStart + 1, detail.quantity);
        addToTotal(feeStart + 4, detail.amount);
      } else {
        feeData.push(0); // Price
        feeData.push(0); // Qty
        feeData.push(""); // Unit
        feeData.push(0); // Discount
        feeData.push(0); // Amount
        feeData.push(""); // Note
      }
    });

    const row = worksheet.addRow([...standardData, ...attendanceData, ...financeData, ...feeData]);
    row.eachCell((cell, colNumber) => {
      cell.font = { name: 'Times New Roman', size: 11 };
      cell.border = {
        top: { style: 'thin' }, left: { style: 'thin' },
        bottom: { style: 'thin' }, right: { style: 'thin' }
      };
      
      const colIdx = colNumber - 1;
      const headerName = allHeaders[colIdx];
      
      // Attendance background
      if (colIdx >= standardHeaders.length && colIdx < standardHeaders.length + attendanceHeaders.length) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9F9F9' } };
      }

      if ([1, 6, 7].includes(colNumber) || (colIdx >= standardHeaders.length && colIdx < standardHeaders.length + attendanceHeaders.length)) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      } else if (colIdx >= standardHeaders.length + attendanceHeaders.length && colIdx < standardHeaders.length + attendanceHeaders.length + financeHeaders.length) {
        if (headerName === "Trạng thái" || headerName === "Ghi chú phiếu thu") {
            cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        } else {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
            cell.numFmt = '#,##0'; // format number for excel
        }
      } else if (colIdx >= standardHeaders.length + attendanceHeaders.length + financeHeaders.length) { 
        // fee columns
        if (headerName === "Đơn giá" || headerName === "Thành tiền" || headerName === "Giảm giá") {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
            if (headerName !== "Giảm giá" || typeof cell.value === "number") {
                 cell.numFmt = '#,##0';
            }
        } else if (headerName === "Số lượng" || headerName === "Đơn vị") {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else if (headerName === "Ghi chú") {
            cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        }
      } else {
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
      }
    });
  });

  if (invoices.length > 0) {
      const totalRow = worksheet.addRow(totals);
      totalRow.eachCell((cell, colNumber) => {
          cell.font = { name: 'Times New Roman', bold: true, size: 11 };
          cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
          };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } }; // Light grey
          
          const colIdx = colNumber - 1;
          const headerName = allHeaders[colIdx];
          
          if ([1, 6, 7].includes(colNumber) || (colIdx >= standardHeaders.length && colIdx < standardHeaders.length + attendanceHeaders.length)) {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          } else if (colIdx >= standardHeaders.length + attendanceHeaders.length && colIdx < standardHeaders.length + attendanceHeaders.length + financeHeaders.length) {
            if (headerName === "Trạng thái" || headerName === "Ghi chú phiếu thu") {
                cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            } else {
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
                if (typeof cell.value === "number") cell.numFmt = '#,##0';
            }
          } else if (colIdx >= standardHeaders.length + attendanceHeaders.length + financeHeaders.length) { 
            // fee columns
            if (headerName === "Đơn giá" || headerName === "Thành tiền" || headerName === "Giảm giá") {
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
                if (typeof cell.value === "number") cell.numFmt = '#,##0';
            } else if (headerName === "Số lượng" || headerName === "Đơn vị") {
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            } else if (headerName === "Ghi chú") {
                cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            }
          } else {
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
            if (colNumber === 2) cell.alignment = { horizontal: 'center', vertical: 'middle' }; // "Tổng cộng"
          }
      });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const timeStamp = moment().format('YYYYMMDD_HHmmss');
  saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Danh_Sach_Phieu_Thu_${timeStamp}.xlsx`);
}
